
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { put } from '@/services/http';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { EventBus } from '@/main';

@Component
export default class POFinancialEditDialog extends Vue {
  @Prop({ type: Boolean, default: false }) value!: boolean;
  @Prop({ type: Number, required: true }) purchaseOrderId!: number;
  @Prop({ type: Object, required: true }) purchaseOrderExtra!: any;
  @Prop({ type: Number, default: 0 }) subtotal!: number;
  @Prop({ type: Number, default: 0 }) totalUnits!: number;
  @Prop({ type: String, default: 'draft' }) purchaseOrderStatus!: string;
  @Prop({ type: Number, default: 0 }) itemTaxTotal!: number;
  @Prop({ type: String, default: 'USD' }) currencyCode!: string;
  @Prop({ type: String, default: 'USD' }) storeCurrency!: string;
  @Prop({ type: Boolean, default: false }) isForeignCurrency!: boolean;
  @Prop({ type: Number, default: null }) exchangeRate!: number | null;

  private show = false;
  private saving = false;
  private errorMessage = '';

  // Local editable fields
  private shippingCost: number = 0;
  private shippingTaxRate: number = 0;
  private discountAmount: number = 0;
  private discountType: 'fixed' | 'percentage' = 'fixed';
  private adjustmentAmount: number = 0;
  private adjustmentNote: string = '';

  @Watch('value', { immediate: true })
  onValueChange(val: boolean): void {
    this.show = val;
    if (val) {
      this.initFromExtra();
    }
  }

  @Watch('show')
  onShowChange(val: boolean): void {
    this.$emit('input', val);
  }

  private initFromExtra(): void {
    const financial = this.purchaseOrderExtra?.financial || {};
    this.shippingCost = Number(financial.shipping_cost) || 0;
    this.shippingTaxRate = (Number(financial.shipping_tax_rate) || 0) * 100; // Convert decimal to percentage for display
    this.discountAmount = Number(financial.discount_amount) || 0;
    this.discountType = financial.discount_type || 'fixed';
    this.adjustmentAmount = Number(financial.adjustment_amount) || 0;
    this.adjustmentNote = financial.adjustment_note || '';
    this.errorMessage = '';
  }

  private get isDraft(): boolean {
    return this.purchaseOrderStatus === 'draft';
  }

  private roundForCurrency(value: number): number {
    return CurrencyHelper.roundForCurrency(value, this.currencyCode);
  }

  private get computedShippingTaxAmount(): number {
    const cost = Number(this.shippingCost) || 0;
    const rate = Number(this.shippingTaxRate) || 0;
    return this.roundForCurrency(cost * (rate / 100));
  }

  private get computedDiscountCalculated(): number {
    const amount = Number(this.discountAmount) || 0;
    if (amount <= 0) return 0;
    if (this.discountType === 'percentage') {
      const clampedPercent = Math.min(Math.max(amount, 0), 100);
      return this.roundForCurrency(this.subtotal * clampedPercent / 100);
    }
    return this.roundForCurrency(amount);
  }

  private get grandTotal(): number {
    const sub = this.subtotal;
    const itemTax = Number(this.itemTaxTotal) || 0;
    const shipping = Number(this.shippingCost) || 0;
    const shippingTax = this.computedShippingTaxAmount;
    const discount = this.computedDiscountCalculated;
    const adjustment = Number(this.adjustmentAmount) || 0;
    return this.roundForCurrency(sub + itemTax + shipping + shippingTax - discount + adjustment);
  }

  formatCurrency(value: number | undefined): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.currencyCode);
  }

  formatCurrencyInStore(value: number | undefined | null): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.storeCurrency);
  }

  get currencyStep(): string {
    return CurrencyHelper.getCurrencyStep(this.currencyCode);
  }

  get currencySymbol(): string {
    return CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  private close(): void {
    this.show = false;
  }

  private async save(): Promise<void> {
    this.saving = true;
    this.errorMessage = '';
    try {
      // Build updated extra with financial data
      const extra = JSON.parse(JSON.stringify(this.purchaseOrderExtra || {}));
      if (!extra.financial) extra.financial = {};
      extra.financial.shipping_cost = Number(this.shippingCost) || 0;
      extra.financial.shipping_tax_rate = (Number(this.shippingTaxRate) || 0) / 100; // Convert percentage back to decimal
      extra.financial.shipping_tax_amount = this.computedShippingTaxAmount;
      extra.financial.discount_amount = this.discountType === 'fixed'
        ? this.roundForCurrency(Number(this.discountAmount) || 0)
        : Number(this.discountAmount) || 0;
      extra.financial.discount_type = this.discountType;
      extra.financial.discount_calculated = this.computedDiscountCalculated;
      extra.financial.adjustment_amount = Number(this.adjustmentAmount) || 0;
      extra.financial.adjustment_note = this.adjustmentNote || '';

      await put(`/purchase_orders/${this.purchaseOrderId}`, { extra });

      // Refresh PO data to get recalculated total from backend
      const updated = await PurchaseOrderModel.getById(this.purchaseOrderId);

      this.$emit('saved', {
        totalAmount: updated.totalAmount,
        extra: updated.extra,
        items: updated.items,
      });

      EventBus.$emit('show-snackbar', 'Financial data updated successfully', 'success');
      this.close();
    } catch (error: any) {
      console.error('Error saving financial data:', error);
      this.errorMessage = error.message || 'Failed to update financial data';
    } finally {
      this.saving = false;
    }
  }
}
